import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ShieldCheck, Loader2 } from 'lucide-react';
import { Role } from '@nexa/auth';
import { RoleGate } from '@/auth/role-gate';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

// Operator force-accept / proceed-without-acceptance (notifications.md invariant 3).
//
// Supervisor+ ONLY. The button is wrapped in <RoleGate min={OPS_SUPERVISOR}>, so
// a non-supervisor sees it disabled with an explanatory tooltip (the dialog never
// opens for them). The UI gate is UX — the operator-bff AND cases re-enforce the
// role on the forwarded X-Operator-Context (defense in depth). On confirm we POST
// SEAM-1 with source=OPERATOR_FORCE: the same canonical OfferAccepted, audited,
// `forced: true`. There is no separate "forced" path past the ingress.

interface ForceAcceptDialogProps {
  /** Friendly label for the PNR group, shown in the confirm copy. */
  groupLabel: string;
  /** True while the force mutation is in flight. */
  pending: boolean;
  /** True until the current offer identity is available; confirmation stays disabled. */
  offerHashMissing: boolean;
  /** Error message from a failed force attempt, if any. */
  error?: string | null;
  /** Confirm — proceed without passenger acceptance. */
  onConfirm: (reason: string) => void;
  /** Extra classes for the trigger button (e.g. the action bar's compact size). */
  triggerClassName?: string | undefined;
}

export function ForceAcceptDialog({
  groupLabel,
  pending,
  offerHashMissing,
  error,
  onConfirm,
  triggerClassName,
}: ForceAcceptDialogProps) {
  const { t } = useTranslation('pnrActions');
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');

  return (
    <>
      <RoleGate
        min={Role.OPS_SUPERVISOR}
        tooltip={t('forceAccept.tooltip')}
      >
        <Button
          type="button"
          variant={triggerClassName ? 'ghost' : 'outline'}
          size="sm"
          {...(triggerClassName ? { className: triggerClassName } : {})}
          onClick={() => setOpen(true)}
          data-testid="force-accept-open"
        >
          <ShieldCheck className="h-3.5 w-3.5" aria-hidden />
          {t('forceAccept.action')}
        </Button>
      </RoleGate>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="force-accept-dialog">
          <DialogHeader>
            <DialogTitle>{t('forceAccept.title')}</DialogTitle>
            <DialogDescription>
              {t('forceAccept.descriptionPrefix')}{' '}
              <span className="font-medium">{groupLabel}</span>{' '}
              {t('forceAccept.descriptionSuffix')}
            </DialogDescription>
          </DialogHeader>

          <label className="block text-sm">
            <span className="mb-1 block font-medium">{t('reason.optionalLabel')}</span>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              placeholder={t('forceAccept.reasonPlaceholder')}
              className="w-full rounded-md border border-input bg-background p-2 text-sm"
              data-testid="force-accept-reason"
            />
          </label>

          {offerHashMissing ? (
            <p className="text-xs text-amber-600 dark:text-amber-400" data-testid="force-accept-warning">
              {t('forceAccept.offerHashMissing')}
            </p>
          ) : null}
          {error ? (
            <p className="text-xs text-destructive" data-testid="force-accept-error">
              {error}
            </p>
          ) : null}

          <DialogFooter>
            <Button type="button" variant="ghost" size="sm" onClick={() => setOpen(false)}>
              {t('common:common.cancel')}
            </Button>
            <Button
              type="button"
              variant="destructive"
              size="sm"
              disabled={pending || offerHashMissing}
              onClick={() => onConfirm(reason.trim())}
              data-testid="force-accept-confirm"
            >
              {pending ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden /> : null}
              {t('forceAccept.action')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
